import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import Select from "react-select";
import Sidebar from "../Sidebar";
import Layout from "../Layout";
import { useAuthz } from "../../auth/useAuthz";
import {
  GET_PACKAGE_BUILDERS, PREVIEW_PACKAGE_BUILDER, PREVIEW_PACKAGE_QUOTE, SAVE_PACKAGE_BUILDER,
  PUBLISH_PACKAGE_BUILDER, ARCHIVE_PACKAGE_BUILDER, DELETE_PACKAGE_BUILDER,
} from "./graphql";
import { GET_CATALOG } from "../AdminHomeBuilder/graphql";
import { STATUS_STYLES, formatDate, toLocalInput, fromLocalInput } from "../AdminHomeBuilder/labels";

const TAB = { Female: "Salon", Male: "Bridal" };
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const input = "border rounded-md px-3 py-2 text-sm w-full";

const blank = (gender = "Female") => ({
  id: null,
  name: "",
  gender,
  title: "Build your own package",
  subtitle: "Pick 3 or more services, save up to 20%",
  tiers: [{ minItems: 3, percent: 10 }, { minItems: 4, percent: 15 }, { minItems: 5, percent: 20 }],
  rules: { maxItems: 8, minTotal: 0, maxDiscount: null, maxPerCategory: 2, oneVariantPerArea: true, allowCoupons: false },
  categoryIds: [],
  excludeServiceIds: [],
  startAt: null,
  endAt: null,
  status: "DRAFT",
});

const toInput = (d) => ({
  id: d.id || undefined,
  name: d.name || "Untitled",
  gender: d.gender,
  title: d.title,
  subtitle: d.subtitle,
  tiers: d.tiers.map((t) => ({ minItems: Number(t.minItems), percent: Number(t.percent) })),
  rules: {
    maxItems: Number(d.rules.maxItems) || null,
    minTotal: Number(d.rules.minTotal) || 0,
    maxDiscount: d.rules.maxDiscount ? Number(d.rules.maxDiscount) : null,
    maxPerCategory: d.rules.maxPerCategory ? Number(d.rules.maxPerCategory) : null,
    oneVariantPerArea: !!d.rules.oneVariantPerArea,
    allowCoupons: !!d.rules.allowCoupons,
  },
  categoryIds: d.categoryIds,
  excludeServiceIds: d.excludeServiceIds,
  startAt: d.startAt,
  endAt: d.endAt,
});

const Field = ({ label, help, children }) => (
  <label className="flex flex-col gap-1 text-sm">
    <span className="font-medium">{label}</span>
    {children}
    {help && <span className="text-xs text-gray-500">{help}</span>}
  </label>
);

/** Editor + live preview for one builder. */
const Editor = ({ builder, canEdit, onBack, onChanged }) => {
  const [d, setD] = useState(builder);
  const [dirty, setDirty] = useState(!builder.id);
  const [message, setMessage] = useState(null);
  const [picked, setPicked] = useState([]);
  const catalog = useQuery(GET_CATALOG, { variables: { gender: d.gender } });
  const [save, { loading: saving }] = useMutation(SAVE_PACKAGE_BUILDER);
  const [publish] = useMutation(PUBLISH_PACKAGE_BUILDER);
  const [archive] = useMutation(ARCHIVE_PACKAGE_BUILDER);
  const [runPreview, preview] = useLazyQuery(PREVIEW_PACKAGE_BUILDER, { fetchPolicy: "network-only" });
  const [runQuote, quote] = useLazyQuery(PREVIEW_PACKAGE_QUOTE, { fetchPolicy: "network-only" });

  const data = useMemo(() => toInput(d), [d]);
  useEffect(() => {
    const t = setTimeout(() => runPreview({ variables: { data } }), 400);
    return () => clearTimeout(t);
  }, [data, runPreview]);
  useEffect(() => {
    const t = setTimeout(() => runQuote({ variables: { data, serviceIds: picked } }), 300);
    return () => clearTimeout(t);
  }, [data, picked, runQuote]);

  const set = (patch) => { setD({ ...d, ...patch }); setDirty(true); setMessage(null); };
  const setRule = (patch) => set({ rules: { ...d.rules, ...patch } });
  const categories = (catalog.data?.getHomeBuilderCatalog?.categories || []).filter((c) => !/^\s*under\b/i.test(c.name));
  const services = catalog.data?.getHomeBuilderCatalog?.services || [];
  const catOptions = categories.map((c) => ({ value: c.id, label: c.name }));
  const svcOptions = services.filter((s) => !s.isPackage).map((s) => ({ value: s.id, label: `${s.name} · ₹${s.price}` }));
  const groups = preview.data?.previewPackageBuilder?.builder?.groups || preview.previousData?.previewPackageBuilder?.builder?.groups || [];
  const previewError = preview.data?.previewPackageBuilder?.status === "ERROR" ? preview.data.previewPackageBuilder.message : null;
  const q = quote.data?.previewPackageQuote?.quote || quote.previousData?.previewPackageQuote?.quote;
  const live = d.status === "LIVE";

  const doSave = async () => {
    const res = await save({ variables: { data } });
    const out = res.data?.savePackageBuilder;
    setMessage({ ok: out?.status === "SUCCESS", text: out?.message || "Could not save" });
    if (out?.status === "SUCCESS") { setDirty(false); setD({ ...d, id: out.id }); onChanged(); }
    return out?.status === "SUCCESS" ? out.id : null;
  };
  const doPublish = async () => {
    const id = dirty || !d.id ? await doSave() : d.id;
    if (!id) return;
    const out = (await publish({ variables: { id } })).data?.publishPackageBuilder;
    setMessage({ ok: out?.status === "SUCCESS", text: out?.status === "SUCCESS" ? "Published. Add the \"Build your package\" section to the home layout to show it." : out?.message });
    if (out?.status === "SUCCESS") setD((x) => ({ ...x, status: "LIVE" }));
    onChanged();
  };
  const doArchive = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Unpublish? Packages already in customers' carts stop getting the discount at checkout.")) return;
    const out = (await archive({ variables: { id: d.id } })).data?.archivePackageBuilder;
    setMessage({ ok: true, text: out?.message });
    setD((x) => ({ ...x, status: "ARCHIVED" }));
    onChanged();
  };
  const togglePick = (id) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3 bg-white border rounded-lg p-3 sticky top-0 z-10">
        <button onClick={() => {
          // eslint-disable-next-line no-alert
          if (!dirty || window.confirm("Leave without saving?")) onBack();
        }} className="px-3 py-1.5 rounded-md bg-gray-100 text-sm">← All builders</button>
        <input className="border rounded-md px-3 py-1.5 font-semibold flex-1 min-w-[200px]" value={d.name} placeholder="Name, e.g. Salon builder" disabled={!canEdit} onChange={(e) => set({ name: e.target.value })} aria-label="Name" />
        <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLES[d.status]}`}>{d.status.toLowerCase()}</span>
        {canEdit && (
          <div className="flex gap-2 ml-auto">
            <button onClick={doSave} disabled={!dirty || saving} className="px-4 py-2 rounded-md bg-gray-900 text-white text-sm disabled:opacity-40">{live ? "Save (goes live)" : "Save draft"}</button>
            {!live && <button onClick={doPublish} className="px-4 py-2 rounded-md bg-[#FBB033] text-sm font-semibold">Publish</button>}
            {live && <button onClick={doArchive} className="px-4 py-2 rounded-md bg-red-50 text-red-700 text-sm">Unpublish</button>}
          </div>
        )}
      </div>
      {message && <div className={`text-sm px-3 py-2 rounded-md ${message.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"}`}>{message.text}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4 items-start">
        <div className="flex flex-col gap-4">
          <section className="bg-white border rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Tab">
              <div className="flex gap-2">
                {["Female", "Male"].map((g) => (
                  <button key={g} type="button" disabled={!canEdit || !!d.id} onClick={() => set({ gender: g, categoryIds: [], excludeServiceIds: [] })}
                    className={`px-3 py-1.5 rounded-md text-sm border ${d.gender === g ? "bg-gray-900 text-white" : "bg-white"} disabled:opacity-60`}>{TAB[g]}</button>
                ))}
              </div>
            </Field>
            <div />
            <Field label="Card title (shown on home and the builder)"><input className={input} value={d.title || ""} onChange={(e) => set({ title: e.target.value })} /></Field>
            <Field label="Card subtitle"><input className={input} value={d.subtitle || ""} onChange={(e) => set({ subtitle: e.target.value })} /></Field>
            <Field label="Starts (optional)" help="Leave both empty for an always-on builder. Dated builders (festival offers) win while open.">
              <input type="datetime-local" className={input} value={toLocalInput(d.startAt)} onChange={(e) => set({ startAt: fromLocalInput(e.target.value) })} />
            </Field>
            <Field label="Ends (optional)"><input type="datetime-local" className={input} value={toLocalInput(d.endAt)} onChange={(e) => set({ endAt: fromLocalInput(e.target.value) })} /></Field>
          </section>

          <section className="bg-white border rounded-lg p-4 flex flex-col gap-3">
            <div>
              <h2 className="font-semibold">Discount tiers</h2>
              <p className="text-xs text-gray-500">The more services picked, the bigger the discount. The discount is taken off what the customer would pay for those services (first-time and offer prices included).</p>
            </div>
            {d.tiers.map((t, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className="flex items-center gap-2 text-sm">
                <input type="number" min={2} max={20} className="border rounded-md px-2 py-1.5 w-20" value={t.minItems} onChange={(e) => set({ tiers: d.tiers.map((x, k) => (k === i ? { ...x, minItems: e.target.value } : x)) })} />
                <span>or more services get</span>
                <input type="number" min={1} max={60} className="border rounded-md px-2 py-1.5 w-20" value={t.percent} onChange={(e) => set({ tiers: d.tiers.map((x, k) => (k === i ? { ...x, percent: e.target.value } : x)) })} />
                <span>% off</span>
                <button type="button" onClick={() => set({ tiers: d.tiers.filter((_, k) => k !== i) })} className="ml-auto text-red-600 px-2" aria-label="Remove tier">✕</button>
              </div>
            ))}
            <button type="button" onClick={() => set({ tiers: [...d.tiers, { minItems: (Number(d.tiers[d.tiers.length - 1]?.minItems) || 2) + 1, percent: (Number(d.tiers[d.tiers.length - 1]?.percent) || 5) + 5 }] })}
              className="self-start px-3 py-1.5 rounded-md border border-dashed text-xs">+ Add tier</button>
          </section>

          <section className="bg-white border rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <h2 className="font-semibold md:col-span-2">Rules</h2>
            <Field label="Most services in one package"><input type="number" className={input} value={d.rules.maxItems ?? ""} onChange={(e) => setRule({ maxItems: e.target.value })} /></Field>
            <Field label="Most from one category" help="Empty = no limit"><input type="number" className={input} value={d.rules.maxPerCategory ?? ""} onChange={(e) => setRule({ maxPerCategory: e.target.value })} /></Field>
            <Field label="Minimum package total (₹)" help="0 = none"><input type="number" className={input} value={d.rules.minTotal ?? 0} onChange={(e) => setRule({ minTotal: e.target.value })} /></Field>
            <Field label="Largest discount (₹)" help="Empty = no cap"><input type="number" className={input} value={d.rules.maxDiscount ?? ""} onChange={(e) => setRule({ maxDiscount: e.target.value })} /></Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!d.rules.oneVariantPerArea} onChange={(e) => setRule({ oneVariantPerArea: e.target.checked })} /> Only one type per area (e.g. one Full Leg Wax, any wax type)</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!d.rules.allowCoupons} onChange={(e) => setRule({ allowCoupons: e.target.checked })} /> Coupons can be used on top of the package</label>
          </section>

          <section className="bg-white border rounded-lg p-4 flex flex-col gap-3">
            <h2 className="font-semibold">Which services</h2>
            <Field label="Categories" help="Empty = every category of the tab except price buckets. Fixed packages are never included.">
              <Select isMulti isDisabled={!canEdit} options={catOptions} value={catOptions.filter((o) => d.categoryIds.includes(o.value))} onChange={(v) => set({ categoryIds: v.map((o) => o.value) })} placeholder="All categories" />
            </Field>
            <Field label="Leave out these services">
              <Select isMulti isDisabled={!canEdit} options={svcOptions} value={svcOptions.filter((o) => d.excludeServiceIds.includes(o.value))} onChange={(v) => set({ excludeServiceIds: v.map((o) => o.value) })} placeholder="None" />
            </Field>
          </section>
        </div>

        <aside className="bg-white border rounded-lg p-3 xl:sticky xl:top-20 flex flex-col gap-3">
          <div>
            <h2 className="font-semibold text-sm">Try it</h2>
            <p className="text-xs text-gray-500">Tick services as a customer would. Prices are for a returning customer.</p>
          </div>
          {previewError && <p className="text-sm text-red-600">{previewError}</p>}
          <div className="max-h-[420px] overflow-y-auto border rounded-md divide-y">
            {groups.map((g) => (
              <div key={g.categoryId} className="p-2">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-1">{g.name}</div>
                {g.services.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm py-0.5">
                    <input type="checkbox" checked={picked.includes(s.id)} onChange={() => togglePick(s.id)} />
                    <span className="flex-1">{s.name}</span>
                    <span className="text-gray-500">{inr(s.campaignPrice || s.price)}</span>
                  </label>
                ))}
              </div>
            ))}
            {!groups.length && <p className="p-3 text-sm text-gray-500">No services match.</p>}
          </div>
          {q && (
            <div className="rounded-md bg-[#F3EEFE] p-3 text-sm">
              <div className="flex justify-between"><span>{q.count} services</span><span>{inr(q.subtotal)}</span></div>
              <div className="flex justify-between text-green-700"><span>Package discount {q.percent ? `(${q.percent}%)` : ""}</span><span>−{inr(q.discount)}</span></div>
              <div className="flex justify-between font-semibold border-t mt-1 pt-1"><span>Customer pays</span><span>{inr(q.total)}</span></div>
              {q.nextTier && <p className="text-xs mt-1">Add {q.nextTier.needed} more for {q.nextTier.percent}% off.</p>}
              {q.errors.map((e) => <p key={e} className="text-xs text-red-600 mt-1">{e}</p>)}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

/** Admin page: "Build your package" builders per tab. */
const PackageBuilders = () => {
  const { can } = useAuthz();
  const canEdit = can("banners.edit");
  const list = useQuery(GET_PACKAGE_BUILDERS, { fetchPolicy: "network-only" });
  const [remove] = useMutation(DELETE_PACKAGE_BUILDER);
  const [open, setOpen] = useState(null);
  const builders = list.data?.getPackageBuilders?.builders || [];

  const doDelete = async (b) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${b.name}"?`)) return;
    const out = (await remove({ variables: { id: b.id } })).data?.deletePackageBuilder;
    // eslint-disable-next-line no-alert
    if (out?.status !== "SUCCESS") window.alert(out?.message);
    list.refetch();
  };

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="mb-4 bg-white rounded-lg border p-4">
          <h1 className="text-xl font-semibold">Package builder</h1>
          <p className="text-sm text-gray-500">Customers pick their own services and get a bigger discount the more they pick. Show it on the app home with the &quot;Build your package&quot; section in the Home Builder.</p>
        </div>
        {open ? (
          <Editor key={open.id || "new"} builder={open} canEdit={canEdit} onBack={() => { setOpen(null); list.refetch(); }} onChanged={() => list.refetch()} />
        ) : (
          <div className="bg-white rounded-lg border">
            <div className="flex items-center justify-between p-3 border-b">
              <span className="text-sm text-gray-600">{builders.length} builder{builders.length === 1 ? "" : "s"}</span>
              {canEdit && <button onClick={() => setOpen(blank())} className="px-4 py-2 bg-[#FBB033] rounded-md text-sm font-semibold">+ New builder</button>}
            </div>
            <ul className="divide-y">
              {list.loading && <li className="p-6 text-center text-sm text-gray-500">Loading…</li>}
              {builders.map((b) => (
                <li key={b.id} className="flex flex-wrap items-center gap-3 px-3 py-3">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setOpen(b)} className="font-medium hover:underline">{b.name}</button>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLES[b.status]}`}>{b.status.toLowerCase()}</span>
                      <span className="text-xs text-gray-500">{TAB[b.gender]}</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {b.tiers.map((t) => `${t.minItems}+ → ${t.percent}%`).join(" · ")} · {b.eligibleCount} services
                      {b.startAt || b.endAt ? ` · ${formatDate(b.startAt) || "now"} → ${formatDate(b.endAt) || "no end"}` : ""}
                    </p>
                  </div>
                  <button onClick={() => setOpen(b)} className="px-3 py-1.5 rounded-md bg-gray-100 text-sm">Open</button>
                  {canEdit && b.status !== "LIVE" && <button onClick={() => doDelete(b)} className="px-3 py-1.5 rounded-md hover:bg-red-50 text-red-600 text-sm">Delete</button>}
                </li>
              ))}
              {!list.loading && !builders.length && <li className="p-8 text-center text-sm text-gray-500">No builders yet.</li>}
            </ul>
          </div>
        )}
      </Layout>
    </div>
  );
};

export default PackageBuilders;
